import { json, type LoaderFunctionArgs } from "@remix-run/node";
import { isRouteErrorResponse, type MetaFunction, useLoaderData, useRouteError } from "@remix-run/react";
import { readItems } from "@directus/sdk";
import directus from "~/server/directus.server";
import { Card } from "~/components/EventCard";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { getEventLink } from "~/utils";
import i18n from "~/i18next.server";
import { formatDate } from "~/utils/dates";

export async function loader({ params, request }: LoaderFunctionArgs) {
    const { tag } = params;
    const locale = await i18n.getLocale(request);
    const t = await i18n.getFixedT(locale);

    const tagData = await directus.request(
        readItems("tags", {
            fields: ["internal_name", "slug", { translations: ["name", "tag"] }],
            filter: {
                slug: { _eq: tag },
                translations: {
                    languages_code: { _eq: locale },
                },
            },
            deep: {
                translations: {
                    _filter: {
                        _and: [
                            {
                                languages_code: { _eq: locale },
                            },
                        ],
                    },
                },
            },
        })
    );

    if (!tagData.length) {
        throw new Response("Tag not found", { status: 404 });
    }

    const events = await directus.request(
        readItems("events", {
            fields: ["year", "month", "day", "name", "isRecurring", { translations: ["title", "header", "status", "slug"], image: ["filename_disk"] }],
            sort: ["year", "month", "day"],
            filter: {
                translations: {
                    languages_code: { _eq: locale },
                    tags: { _contains: tagData[0].translations[0].tag }
                }
            },
            limit: 200,
            deep: {
                translations: {
                    _filter: {
                        _and: [
                            {
                                languages_code: { _eq: locale },
                            },
                        ],
                    },
                },
            },
        })
    );

    if (!events.length) {
        throw new Response("No events found for this tag", { status: 404 });
    }

    return json({ 
        events, 
        tagData,
        locale,
        meta: {
            title: t("tags.eventsTitle", { tag: tagData[0].translations[0].name }),
            description: t("tags.description", { 
                tag: tagData[0].translations[0].name,
                count: events.length,
            }),
            keywords: t("meta.tags.keywords", { 
                tag: tagData[0].translations[0].name,
                defaultValue: "historical events, {{tag}}, history timeline, important dates, historical moments, chronology"
            }),
            canonicalUrl: `${process.env.HOST_URL}/tags/${tagData[0].translations[0].slug}`,
            locale,
            type: "website",
        }
    });
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
    if (!data?.meta) return [];

    return [
        { title: data.meta.title },
        { name: "description", content: data.meta.description },
        { name: "keywords", content: data.meta.keywords },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: data.meta.title },
        { property: "og:description", content: data.meta.description },
        { property: "og:type", content: data.meta.type },
        { property: "og:url", content: data.meta.canonicalUrl },
        { property: "og:locale", content: data.meta.locale },
        { property: "og:site_name", content: data.meta.locale === "pl" ? "CoKiedy.pl" : "WhatWhen.day" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: data.meta.title },
        { name: "twitter:description", content: data.meta.description },
        { rel: "canonical", href: data.meta.canonicalUrl },
        { name: "format-detection", content: "telephone=no" }
    ];
};

export function ErrorBoundary() {
    const error = useRouteError();
    const { t } = useTranslation();

    if (isRouteErrorResponse(error) && error.status === 404) {
        return (
            <div className="container mx-auto px-4 py-16 text-center">
                <h1 className="text-4xl font-bold mb-4">{t('404.title')}</h1>
                <p className="text-lg mb-8">{t('404.message')}</p>
                <a href="/" className="text-blue-600 hover:text-blue-800 underline">
                    {t('backToHome')}
                </a>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-16 text-center">
            <h1 className="text-4xl font-bold mb-4">{t('common.errorTitle')}</h1>
            <p className="text-lg mb-8">{t('common.errorMessage')}</p>
        </div>
    );
}

export const headers = {
    "Cache-Control": "public, max-age=14400, s-maxage=86400, stale-while-revalidate=604800",
};

export default function EventList() {
    const { events, tagData, locale } = useLoaderData<typeof loader>();
    const { t } = useTranslation();

    return (
        <section className="relative">
            <div className="container mx-auto px-4 py-8 max-w-4xl">
                <h1 className="text-3xl font-bold mb-8">{t("tags.eventsTitle", { tag: tagData[0].translations[0].name })}</h1>

                <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1 gap-8">
                    {events.map((event) => (
                        <Card
                            key={event.translations[0].slug}
                            date={formatDate({ year: event.year, month: event.month, day: event.day }, locale)}
                            title={event.translations[0].title}
                            description={event.translations[0].header}
                            status={event.translations[0].status}
                            image={`https://cdn.whatwhen.day/${event.image?.filename_disk}`}
                            to={getEventLink(event)}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
